import { useSelector } from "react-redux";
import { useLocation, useMatches } from "react-router-dom";

import { defaultPageTitleOptions, pageTitle } from "config/constants";
import { RouteHandle } from "router/routes/types";
import { RootState } from "store";
import { isUUID } from "utils/isUUID";

import { filter, isEmpty, isEqual, join, kebabCase, split } from "lodash-es";

import { useTranslation } from "./useTranslation";

const formatPathname = (pathname: string) => {
  const splitPaths = split(pathname, "/");
  const filteredPaths = filter(splitPaths, path => !isEmpty(path) && !isUUID(path));
  const cleanedPathname = join(filteredPaths, "/");
  return kebabCase(cleanedPathname);
};

const buildLocalizationKey = (pathname: string, authenticated: boolean) => {
  const prefix = "routes.metadata.title";
  const indexFallback = authenticated ? "index" : "root";
  const normalizedPathname = isEqual(pathname, "/") ? indexFallback : pathname;
  return `${prefix}.${formatPathname(normalizedPathname)}`;
};

const buildPageTitle = (title: string) => {
  const options = { ...defaultPageTitleOptions, title };
  if (isEmpty(options.title)) {
    return options.baseTitle;
  }
  return pageTitle(options);
};

const computePageTitle = (
  rawTitle: RouteHandle["title"],
  params?: Record<string, string | undefined>
) => {
  if (typeof rawTitle === "function") {
    return rawTitle(params ?? {});
  }

  return rawTitle ?? "";
};

export const usePageTitle = () => {
  const matches = useMatches();
  const location = useLocation();
  const { t } = useTranslation();
  const authenticated = useSelector((state: RootState) => state.authentication.authenticated);

  const deepestTitledMatch = [...matches].reverse().find(m => (m.handle as RouteHandle)?.title);
  const rawTitle = (deepestTitledMatch?.handle as RouteHandle)?.title;
  const computedTitle = computePageTitle(rawTitle, deepestTitledMatch?.params);

  const localizationKey = buildLocalizationKey(location.pathname, !!authenticated);
  const finalTitle = t(localizationKey, computedTitle);

  return buildPageTitle(finalTitle);
};
